import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, useLang, formatCurrency, usePerm } from "../lib/providers";
import { PageHeader, Btn, StatusBadge, EmptyState, TextInput } from "../components/primitives";
import BulkActionBar, { Checkbox } from "../components/BulkActionBar";
import { downloadPdfBundle } from "../lib/bundle";
import { useConfirm } from "../components/ConfirmDialog";
import { Plus, PencilSimple, Trash, MagnifyingGlass, Archive, FileText } from "@phosphor-icons/react";

export default function InvoicesListPage() {
  const { t, lang } = useLang();
  const confirm = useConfirm();
  const { can, isPrivileged } = usePerm();
  const canCreate = can("invoices", "create");
  const canEdit = can("invoices", "edit");
  const canDelete = can("invoices", "delete");
  const canViewArchive = isPrivileged;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [docType, setDocType] = useState("all"); // "all" | "invoice" | "credit_note"
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(new Set());

  const load = (search = "") => {
    setLoading(true);
    api.get(`/invoices${search ? `?q=${encodeURIComponent(search)}` : ""}`).then((r) => setItems(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    const tid = setTimeout(() => load(q), 250);
    return () => clearTimeout(tid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const del = async (id, number) => {
    const ok = await confirm({
      title: `${t("moveToTrash")}: ${number || ""}`,
      description: t("confirmInvoiceDeleteDesc"),
      confirmText: t("moveToTrash"),
      variant: "danger",
    });
    if (!ok) return;
    await api.delete(`/invoices/${id}`);
    load(q);
  };

  const toggleSel = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const bulkDel = async () => {
    const ids = [...selected];
    const ok = await confirm({
      title: t("moveToTrash"),
      description: t("confirmDeleteMany").replace("{n}", ids.length) + " " + t("confirmInvoiceDeleteDesc"),
      confirmText: t("moveToTrash"),
      variant: "danger",
    });
    if (!ok) return;
    await api.post("/invoices/bulk-delete", { ids });
    setSelected(new Set());
    load(q);
  };
  const bulkCopy = async () => {
    const ids = [...selected];
    await api.post("/invoices/bulk-duplicate", { ids });
    setSelected(new Set());
    load(q);
  };

  const docTypeFiltered = (() => {
    if (docType === "all") return items;
    if (docType === "invoice") return items.filter((x) => (x.doc_type || "invoice") === "invoice");
    if (docType === "storno") return items.filter((x) => x.doc_type === "storno");
    if (docType === "proforma") return items.filter((x) => x.doc_type === "proforma");
    if (docType === "credit_full") return items.filter((x) => x.doc_type === "credit_note" && (x.credit_mode || "full") === "full");
    if (docType === "credit_partial") return items.filter((x) => x.doc_type === "credit_note" && x.credit_mode === "partial");
    return items;
  })();
  const filtered = filter === "all" ? docTypeFiltered : docTypeFiltered.filter((x) => x.status === filter);
  const counts = docTypeFiltered.reduce((acc, x) => ({ ...acc, [x.status]: (acc[x.status] || 0) + 1 }), {});
  const docTypeCounts = items.reduce((acc, x) => {
    const dt = x.doc_type || "invoice";
    if (dt === "invoice") acc.invoice = (acc.invoice || 0) + 1;
    else if (dt === "storno") acc.storno = (acc.storno || 0) + 1;
    else if (dt === "proforma") acc.proforma = (acc.proforma || 0) + 1;
    else if (dt === "credit_note") {
      if ((x.credit_mode || "full") === "partial") acc.credit_partial = (acc.credit_partial || 0) + 1;
      else acc.credit_full = (acc.credit_full || 0) + 1;
    }
    return acc;
  }, {});

  const docTypeChips = [
    { k: "all",            label: lang === "de" ? "Alle" : "All",                                  n: items.length },
    { k: "invoice",        label: lang === "de" ? "Rechnungen" : "Invoices",                       n: docTypeCounts.invoice || 0 },
    { k: "storno",         label: lang === "de" ? "Stornos" : "Cancellations",                     n: docTypeCounts.storno || 0 },
    { k: "proforma",       label: "Proforma",                                                      n: docTypeCounts.proforma || 0 },
    { k: "credit_full",    label: lang === "de" ? "Gutschriften" : "Credit notes",                 n: docTypeCounts.credit_full || 0 },
    { k: "credit_partial", label: lang === "de" ? "Teilgutschriften" : "Partial credit notes",      n: docTypeCounts.credit_partial || 0 },
  ];

  const chips = [
    { k: "all",       label: lang === "de" ? "Alle" : "All",  n: docTypeFiltered.length },
    { k: "draft",     label: t("draft"),     n: counts.draft || 0 },
    { k: "issued",    label: t("issued"),    n: counts.issued || 0 },
    { k: "paid",      label: t("paid"),      n: counts.paid || 0 },
    { k: "overdue",   label: t("overdue"),   n: counts.overdue || 0 },
    { k: "cancelled", label: t("cancelled"), n: counts.cancelled || 0 },
  ];

  return (
    <div className="p-6 sm:p-8 max-w-[1400px] mx-auto">
      <PageHeader
        eyebrow="07 / Accounting"
        title={t("invoices")}
        description={lang === "de" ? "Alle Rechnungen — inkl. ZUGFeRD / XRechnung Export." : "All invoices including ZUGFeRD / XRechnung export."}
        actions={
          <div className="flex gap-2">
            {canViewArchive && (
              <Link to="/invoices/archive">
                <Btn variant="secondary" data-testid="goto-archive"><Archive size={16} /> {t("archive")}</Btn>
              </Link>
            )}
            {canCreate && (
              <>
                <Link to="/invoices/new?type=proforma">
                  <Btn variant="secondary" data-testid="new-proforma-btn"><FileText size={16} /> + {t("newProforma")}</Btn>
                </Link>
                <Link to="/invoices/new">
                  <Btn data-testid="new-invoice-btn"><Plus size={16} /> {t("newInvoice")}</Btn>
                </Link>
              </>
            )}
          </div>
        }
      />

      <div className="mb-4">
        <div className="flex gap-2 mb-3 border-b border-[var(--border)]">
          {docTypeChips.map((c) => (
            <button
              key={c.k}
              onClick={() => setDocType(c.k)}
              data-testid={`doctype-${c.k}`}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors -mb-px ${
                docType === c.k
                  ? "border-[var(--brand)] text-[var(--brand)]"
                  : "border-transparent text-[var(--muted)] hover:text-[var(--ink)]"
              }`}
            >
              {c.label} <span className="opacity-60 ml-1 text-xs">{c.n}</span>
            </button>
          ))}
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <div className="relative flex-1 min-w-[220px] max-w-sm">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--muted-2)]" />
            <TextInput data-testid="invoices-search" placeholder={t("search")} value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" />
          </div>
          {chips.map((c) => (
            <button key={c.k} onClick={() => setFilter(c.k)} data-testid={`filter-${c.k}`}
              className={`px-3 h-8 text-xs rounded-sm border transition-colors ${
                filter === c.k
                  ? "bg-[var(--brand)] text-white border-[var(--brand)]"
                  : "bg-[var(--surface)] text-[var(--muted)] border-[var(--border)] hover:bg-[var(--row-hover)]"
              }`}>
              {c.label} <span className="opacity-60 ml-1">{c.n}</span>
            </button>
          ))}
        </div>
      </div>

      <BulkActionBar
        count={selected.size}
        lang={lang}
        onClear={() => setSelected(new Set())}
        onCopy={canCreate ? bulkCopy : undefined}
        onDelete={canDelete ? bulkDel : undefined}
        deleteLabel={t("moveToTrash")}
        onBundle={async () => {
          const ids = [...selected];
          const items = ids.map((id) => ({ type: "invoice", id }));
          await downloadPdfBundle(items, `Rechnungen_Bundle_${new Date().toISOString().slice(0,10)}`, lang);
        }}
      />

      {loading ? (
        <div className="swiss-card p-8 text-[var(--muted)]">…</div>
      ) : filtered.length === 0 ? (
        <EmptyState
          title={t("noInvoices")}
          description={lang === "de" ? "Erstelle eine Rechnung aus einem Angebot oder direkt neu." : "Create an invoice from a quote or directly."}
          action={canCreate ? <Link to="/invoices/new"><Btn data-testid="empty-new-invoice"><Plus size={16} /> {t("newInvoice")}</Btn></Link> : null}
        />
      ) : (
        <div className="swiss-card overflow-hidden">
          <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[720px]">
            <thead className="bg-[var(--bg)] text-[var(--muted)]">
              <tr>
                {(canEdit || canDelete) && <th className="w-8 pl-5"></th>}
                <th className="text-left font-medium px-5 py-3 label-overline">{t("number")}</th>
                <th className="text-left font-medium px-5 py-3 label-overline">{t("invoiceDate")}</th>
                <th className="text-left font-medium px-5 py-3 label-overline">{t("title")}</th>
                <th className="text-left font-medium px-5 py-3 label-overline">{t("customer")}</th>
                <th className="text-left font-medium px-5 py-3 label-overline">{t("status")}</th>
                <th className="text-right font-medium px-5 py-3 label-overline">{t("total")}</th>
                {(canEdit || canDelete) && <th className="text-right font-medium px-5 py-3 label-overline">{t("actions")}</th>}
              </tr>
            </thead>
            <tbody>
              {filtered.map((x) => (
                <tr key={x.id} className={`border-t border-[var(--border)] transition-colors fade-up ${selected.has(x.id) ? "bg-[var(--brand-soft)]" : "hover:bg-[var(--row-hover)]"}`}>
                  {(canEdit || canDelete) && (
                    <td className="pl-5">
                      <Checkbox checked={selected.has(x.id)} onChange={() => toggleSel(x.id)} testId={`sel-invoice-${x.id}`} />
                    </td>
                  )}
                  <td className="px-5 py-3 font-mono">
                    <div className="flex items-center gap-2">
                      <span>{x.number}</span>
                      {x.doc_type === "credit_note" && (
                        x.credit_mode === "partial" ? (
                          <span className="text-[10px] px-1.5 py-0.5 rounded-sm bg-[#F59E0B]/15 text-[#B45309] font-sans" data-testid={`badge-credit-partial-${x.id}`}>
                            {lang === "de" ? "Teil-GS" : "Partial CN"}
                          </span>
                        ) : (
                          <span className="text-[10px] px-1.5 py-0.5 rounded-sm bg-[#F59E0B]/15 text-[#B45309] font-sans" data-testid={`badge-credit-${x.id}`}>
                            {lang === "de" ? "GS" : "CN"}
                          </span>
                        )
                      )}
                    </div>
                  </td>
                  <td className="px-5 py-3 text-[var(--muted)]">{(x.invoice_date || "").slice(0, 10)}</td>
                  <td className="px-5 py-3 font-medium">
                    <Link to={`/invoices/${x.id}`} className="hover:underline" data-testid={`invoice-link-${x.id}`}>
                      {x.title || <span className="text-[var(--muted-2)]">—</span>}
                    </Link>
                  </td>
                  <td className="px-5 py-3 text-[var(--muted)]">{x.customer_snapshot?.name || "—"}</td>
                  <td className="px-5 py-3"><StatusBadge status={x.status} lang={lang} /></td>
                  <td className="px-5 py-3 text-right font-medium">{formatCurrency(x.total, lang)}</td>
                  {(canEdit || canDelete) && (
                    <td className="px-5 py-3 text-right">
                      <div className="inline-flex gap-1">
                        <Link to={`/invoices/${x.id}`}><Btn variant="ghost" className="h-8 px-2" data-testid={`edit-invoice-${x.id}`}><PencilSimple size={14} /></Btn></Link>
                        {canDelete && <Btn variant="ghost" onClick={() => del(x.id, x.number)} className="h-8 px-2 text-[#FF3B30] hover:bg-[#FF3B30]/5" data-testid={`delete-invoice-${x.id}`}><Trash size={14} /></Btn>}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}
    </div>
  );
}
